import {PayloadAction, createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {IFetchPOApprovedParams, IGetSIPParams} from '../../api/modules/master-data/types'
import {getPOApprovedSearch, getSIPSearch} from '../../api/modules/sip'
import * as T from '../../services/types'
import {RootState} from '../../store'
import {TOrder} from '../types'

export interface ISort {
  sipNumber: 'asc' | 'desc' | string
  customer: 'asc' | 'desc' | string
}

export interface IPDFPreviewSIP {
  sip_number?: string
  regarding?: string
  sector_id?: string
  signer_name?: string
  from_date?: string
  to_date?: string
  submission_date?: string
  receiver_name?: string
  customer_code?: string
  customer_name?: string
  signer_position?: string
  payment_method?: string
  address?: string
  description?: string
  items?: any[]
}

export interface ISIPState {
  isLoading?: boolean
  error?: string
  sipOrders?: Array<TOrder>
  approvedOrders?: Array<TOrder>
  selectedOrder?: TOrder
  filter: T.IFetchSIPOrdersParams
  sort?: ISort
  numberUrls?: Array<string>
  filterPOApproved?: IFetchPOApprovedParams
  pdfPreview?: IPDFPreviewSIP
}

export const initialState: ISIPState = {
  isLoading: false,
  error: '',
  sipOrders: [],
  sort: {
    sipNumber: '',
    customer: '',
  },
  filter: {
    po_number: '',
    from_date: '',
    to_date: '',
    sales_org_code: '',
    customer_name: '',
    perihal: '',
    total_quantum: '',
    estimate_date: '',
    status: '',
    sector_id: '',
    created_at: '',
    po_number_orderby: '',
    customer_name_orderby: '',
    price: '',
    product_type: '',
    sales_org_name: '',
    SIP_number: '',
    total_request_price: '',
    total_request_quantum: '',
    per_page: 25,
    outstanding_data: '',
    flag: '',
  },
  numberUrls: [],
  filterPOApproved: {
    po_number: '',
    customer_name: '',
    sector_id: '',
  },
  pdfPreview: {
    sip_number: '',
    regarding: '',
    sector_id: '',
    signer_name: '',
    from_date: '',
    to_date: '',
    submission_date: '',
    receiver_name: '',
    address: '',
    customer_name: '',
    customer_code: '',
    signer_position: '',
    description: '',
    payment_method: '',
    items: [],
  },
}

/**
 * Request search sip orders
 * @note: Under development
 */
export const searchSipOrders = createAsyncThunk(
  'sip/searchSipOrders',
  async (params: any, thunkAPI) => {
    try {
      const credential = localStorage.getItem('credential')
      const {po_number, from_date, to_date} = (thunkAPI.getState() as RootState).sipReducer
        .filter as IGetSIPParams
      const tmpOrderNumber = po_number?.replace('A', '').replace('a', '')
      const {data} = await getSIPSearch(credential, tmpOrderNumber, from_date, to_date)
      if (data.error) return thunkAPI.rejectWithValue(data)
      return data.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

export const fetchApprovedOrders = createAsyncThunk(
  'sip/fetchApprovedOrders',
  async (params: Partial<IGetSIPParams>, thunkAPI) => {
    try {
      const res = await getPOApprovedSearch(
        params?.po_number ?? '',
        params?.customer_name ?? '',
        params?.sector_id ?? ''
      )
      return res.data.data
    } catch (error) {
      return thunkAPI.rejectWithValue(error)
    }
  }
)

/**
 * Slice
 */
const sipSlice = createSlice({
  name: 'sip',
  initialState,
  reducers: {
    setSipOrders: (state: ISIPState, action: PayloadAction<TOrder[]>) => ({
      ...state,
      orders: action.payload,
    }),
    setApprovedOrders: (state: ISIPState, action: PayloadAction<TOrder[]>) => ({
      ...state,
      approvedOrders: action.payload,
    }),
    setFilter: (state: ISIPState, action: PayloadAction<T.IFetchSIPOrdersParams>) => {
      try{
        localStorage.setItem('redux-sip-filter', JSON.stringify(action.payload) as string)
      }catch(e){}
      state.filter = action.payload
    },
    setSort: (state: ISIPState, action: PayloadAction<ISort>) => ({
      ...state,
      sort: action.payload,
    }),
    setError: (state: ISIPState, action: PayloadAction<any>) => ({
      ...state,
      error: action.payload,
    }),
    setIsLoading: (state: ISIPState, action: PayloadAction<boolean>) => ({
      ...state,
      isLoading: action.payload,
    }),
    resetFilter: (state: ISIPState) => {
      localStorage.removeItem('redux-sip-filter')
      state.filter = initialState.filter
    },
    // ({
    //   ...state,
    //   filter: initialState.filter,
    // }),
    setNumberUrls: (state: ISIPState, action: PayloadAction<string[]>) => ({
      ...state,
      numberUrls: action.payload,
    }),
    setSelectedOrder: (state: ISIPState, action: PayloadAction<TOrder>) => ({
      ...state,
      selectedOrder: action.payload,
    }),
    setFilterPOApproved: (state: ISIPState, action: PayloadAction<IFetchPOApprovedParams>) => ({
      ...state,
      filterPOApproved: action.payload,
    }),
    resetFilterPOApproved: (state: ISIPState) => ({
      ...state,
      filterPOApproved: initialState.filterPOApproved,
    }),
    setPDFPreview: (state: ISIPState, action: PayloadAction<IPDFPreviewSIP>) => ({
      ...state,
      pdfPreview: action.payload,
    }),
  },
  extraReducers(builder) {
    builder.addCase(searchSipOrders.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(searchSipOrders.fulfilled, (state, action) => {
      state.isLoading = false
      state.sipOrders = action.payload
    })
    builder.addCase(searchSipOrders.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })

    builder.addCase(fetchApprovedOrders.pending, (state) => {
      state.isLoading = true
    })
    builder.addCase(fetchApprovedOrders.fulfilled, (state, action) => {
      state.isLoading = false
      state.approvedOrders = action.payload.data
      state.numberUrls = action.payload.links
    })
    builder.addCase(fetchApprovedOrders.rejected, (state, action) => {
      state.isLoading = false
      state.error = action.payload as string
    })
  },
})

/**
 * Action
 */
export const {
  setSipOrders,
  setApprovedOrders,
  setFilter,
  setSort,
  setError,
  setIsLoading,
  resetFilter,
  setNumberUrls,
  setSelectedOrder,
  setFilterPOApproved,
  resetFilterPOApproved,
  setPDFPreview,
} = sipSlice.actions

/**
 * Selector
 */
export const sipSelector = (state: RootState) => state.sipReducer

/**
 * Reducer
 */
export default sipSlice.reducer
